import RefreshIcon from '@mui/icons-material/Refresh';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional override for the fallback UI. Receives the caught error. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Hook for external error reporters (Sentry, Datadog, etc). */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary for the whole app. Catches render errors
 * thrown by descendants (but NOT async errors or event-handler throws
 * — those still go to `window.onerror`/`unhandledrejection`).
 *
 * Intentionally a class component: React's official error boundary API
 * still requires one.
 */
export class RootErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);

    console.error('[RootErrorBoundary]', error, info);
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }

    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 4 }}>
        <Stack spacing={3} sx={{ maxWidth: 560, width: '100%' }}>
          <Alert severity="error" variant="outlined">
            <AlertTitle>Something went wrong</AlertTitle>
            <Typography variant="body2" sx={{ mb: 1 }}>
              The app hit an unexpected error. Your work is safe — try refreshing, or reload the
              page if the issue persists.
            </Typography>
            {import.meta.env.DEV && (
              <Box
                component="pre"
                sx={{
                  m: 0,
                  mt: 1,
                  p: 1.5,
                  borderRadius: 1,
                  fontSize: 12,
                  overflow: 'auto',
                  bgcolor: 'action.hover',
                }}
              >
                {error.stack ?? error.message}
              </Box>
            )}
          </Alert>
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            <Button onClick={this.reset} startIcon={<RefreshIcon />} variant="contained">
              Try again
            </Button>
            <Button
              onClick={() => {
                window.location.reload();
              }}
              color="inherit"
            >
              Reload page
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }
}
